// Lista-oldal „Hozzáadás" gombja — EGYSÉGES minta (a Clients-oldal referenciája): default Button
// + PlusIcon + címke, a toolbar jobb szélén (`hidden md:block` — mobilon az AppShell `primaryAction`
// FAB-ja kezeli a létrehozást). Jog hiányában a gomb disabled + tooltip a hiány okával.
//
// SZÍN-KONVENCIÓ (design.md): a `default` (GRADIENSES) variáns KIZÁRÓLAG ezé a gombé — a lista-oldal
// „+ Új …" belépőjéé. Minden más elsődleges akció (mentés/létrehozás/megerősítés) a LAPOS zöld
// `variant="brand"`. Így a gradiens egyetlen dolgot jelent: „innen indul új rekord".
import { PlusIcon } from '@heroicons/react/24/outline';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ListNewButtonProps {
  label: string;
  onClick: () => void;
  /** Van-e létrehozási jog (általában `can('<mod>.manage')`). */
  enabled: boolean;
  /** A tooltip szövege, ha nincs jog (pl. „Nincs jogosultság a létrehozáshoz."). */
  disabledTooltip: string;
}

export function ListNewButton({
  label,
  onClick,
  enabled,
  disabledTooltip,
}: ListNewButtonProps): JSX.Element {
  if (enabled) {
    return (
      <Button variant="cta" onClick={onClick}>
        <PlusIcon className="h-4 w-4 stroke-2" />
        {label}
      </Button>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* disabled gomb — a tooltip a fókuszálható span-en (a disabled nem tüzel pointer-eventet). */}
        <span tabIndex={0}>
          <Button variant="cta" disabled>
            <PlusIcon className="h-4 w-4 stroke-2" />
            {label}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{disabledTooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A „+ Új …" belépő MENÜ-TRIGGER változata — ugyanaz a gradiens-gomb, amit egy `DropdownMenuTrigger asChild` kapcsol
 * (a kezdőlap fejlécének „Új…" menüje: egy gomb, több cél). A gradiens-jelentés változatlan: „innen indul új rekord".
 */
export const ListNewMenuTrigger = forwardRef<
  HTMLButtonElement,
  { label: string } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>
>(({ label, ...props }, ref) => (
  <Button ref={ref} variant="cta" {...props}>
    <PlusIcon className="h-4 w-4 stroke-2" aria-hidden />
    {label}
  </Button>
));
ListNewMenuTrigger.displayName = 'ListNewMenuTrigger';
